import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment";
import axios from "axios";
import Cookies from "universal-cookie";
import { Button, notification, Tooltip, Card, Image, Modal, Select } from "antd";
import _, { map, sortBy, trim, findIndex } from "lodash";

import { saveAsDraftNewOrder } from "modules/client/active-orders/store/orderSlice";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import { resetNewOrderStatuses } from "modules/client/active-orders/store/orderSlice";
import { setGlobalLoading } from "app/global/slice";
import { generateId, replaceElementInArray } from "helper/util";
import { TOKEN_NAME } from "configs/constants";

import "./index.scss";
import { CloudUploadOutlined, EditFilled } from "@ant-design/icons";
import ReactMapboxGl from "react-mapbox-gl";
import AddressInput from "components/inputs/AddressInput";
import DefaultMapbox from "modules/client/active-orders/components/mapbox/DefaultMapbox";
import request from "helper/request";
import { ProfileContext } from "layouts/private/ProfileContext";

const BASE_URL = window.REACT_APP_API_ENDPOINT;

const Map = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_KEY,
  onStyleLoad: (m) => m.resize(),
  zoom: 5
});

const COLUMN_CANNOT_NULL = [
  "Id",
  "Stop Type",
  "Pickup Id",
  "Stop Name",
  "Stop Address",
  "Stop Time Window - Start",
  "Stop Time Window - End",
  "Waiting/Service Time"
];
const PHONE_COLUMNS = ["Contact Phone", "Contact Phone 1", "Contact Phone 2"];
const LAT_LNG_DATA = ["Stop Latitude", "Stop Longitude"];
const PACKAGE_DATA = {
  "Total Micro Packages": "Micro",
  "Total Small Packages": "Small",
  "Total Medium Packages": "Medium",
  "Total Large Packages": "Large",
  "Total Pallet Packages": "Pallet",
  "Total Other Packages": "Custom",
  "Total Postal Packages": "Postal (sobre)"
};
const TIME_KEYS = ["Stop Time Window - Start", "Stop Time Window - End"];
const MAP_CSV_DATA = {
  Id: "id",
  "Stop Type": "type",
  "Contact Name": "contact_name",
  "Contact Email": "email",
  Notes: "note",
  "Stop Name": "location_name",
  "Stop Address": "physical_address",
  "Stop Time Window - Start": "from_time",
  "Stop Time Window - End": "to_time",
  "Waiting/Service Time": "duration",
  "Internal Route ID": "internalRouteId",
  // Optional. Splits a "Prerouted - keep order" import into routes. Kept separate from
  // Internal Route ID, which clients use as a tracking ID. Both casings are accepted because
  // the header is typed by hand; absent from COLUMN_CANNOT_NULL so existing files still import.
  "Prerouted Route ID": "preroutedRouteId",
  "Prerouted Route Id": "preroutedRouteId",
  // Optional. Lets a pre-optimized file state its sequence explicitly instead of relying on
  // row order. Deliberately absent from COLUMN_CANNOT_NULL — files without it still import.
  "Stop Sequence": "stopSequence",
  "Internal Order ID": "internalOrderId",
  "Internal Customer ID": "internalCustomerId",
  Salesperson: "salesPerson",
  COD: "cod"
};
const COLUMN_NAME_BARCODE_PACKAGE = (packageName) =>
  `Barcode Number ${packageName} Packages`;
const ID_INDEX = "Id";

const ImportLocations = ({
  handleCloseModal,
  isAdmin = false,
  setTotalStop,
  setCurrentStop,
  aiImportData = null
}) => {
  const [error, setError] = useState(false);
  const [mapAVLError, setMapAVLError] = useState(false);
  const [clientId, setClientId] = useState("");
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx.profile);
  const [allClients, setAllClients] = useState([]);
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [mapAddress, setMapAddress] = useState({});
  const [avlRowAddressError, setAVLRowAddressError] = useState([]);
  const [importRowIdSelected, setImportRowIdSelected] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  const packageTypeList = useSelector(selectAllPackageTypes);
  const [array, setArray] = useState([]);
  const [originalFile, setOriginalFile] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    if (avlRowAddressError.length) {
      setMapAVLError(true);
    } else {
      setMapAVLError(false);
    }
  }, [avlRowAddressError, mapAVLError]);

  useEffect(() => {
    const fetchClients = async () => {
      const tmpClients = await request.get("/admin/clients/all-clients", {
        params: { includeIndividuals: true }
      });
      setAllClients(tmpClients);
    };
    fetchClients();
    // eslint-disable-next-line
  }, []);

  // Helper function to parse CSV line respecting quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        // Field separator (only when not in quotes)
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    // Push the last field
    result.push(current.trim());

    return result;
  };

  // Auto-process CSV data from AI Import
  useEffect(() => {
    if (aiImportData) {
      // Clear any previous processing state
      setIsProcessing(false);
      console.log("[AI Import] Raw CSV data received:", aiImportData);

      // Convert CSV string to XLSX-like format for processing
      const rows = aiImportData.split("\n").filter((row) => row.trim());
      console.log("[AI Import] Total rows after filtering:", rows.length);

      if (rows.length > 0) {
        // Parse headers using CSV parser
        const headers = parseCSVLine(rows[0]);
        console.log("[AI Import] Parsed headers:", headers);
        console.log("[AI Import] Number of columns:", headers.length);

        // Parse data rows
        const data = rows.slice(1).map((row, rowIndex) => {
          const values = parseCSVLine(row);
          console.log(
            `[AI Import] Row ${rowIndex + 1} parsed into ${values.length} columns:`,
            values
          );

          const rowData = {};
          headers.forEach((header, i) => {
            rowData[header] = values[i] || "";
          });

          // Log specific columns for debugging
          if (rowIndex < 3) {
            // Log first 3 rows in detail
            console.log(
              `[AI Import] Row ${rowIndex + 1} - Stop Name:`,
              rowData["Stop Name"]
            );
            console.log(
              `[AI Import] Row ${rowIndex + 1} - Stop Address:`,
              rowData["Stop Address"]
            );
            console.log(
              `[AI Import] Row ${rowIndex + 1} - Stop Latitude:`,
              rowData["Stop Latitude"]
            );
            console.log(
              `[AI Import] Row ${rowIndex + 1} - Stop Longitude:`,
              rowData["Stop Longitude"]
            );
          }

          return rowData;
        });

        console.log("[AI Import] Total data rows parsed:", data.length);
        console.log("[AI Import] First row data:", data[0]);

        const tmpData = sortBy(data, function (o) {
          return o["Stop Type"] !== "Pickup";
        });

        console.log("[AI Import] Data sorted, processing for errors...");

        const processData = async () => {
          const [resultArr, isError] = await checkErrors(tmpData);
          console.log("[AI Import] Error check complete. Has errors:", isError);
          console.log("[AI Import] Result array length:", resultArr.length);
          if (isError) {
            console.log(
              "[AI Import] Errors found in rows:",
              resultArr
                .filter((r) => r.error)
                .map((r, i) => ({ row: i + 1, error: r.error }))
            );
          }
          setError(isError);
          setArray(resultArr);
        };

        processData();
      }
    }
    // eslint-disable-next-line
  }, [aiImportData]);

  const onImportExcel = (tmpFile) => {
    //  gets the uploaded file object
    const { files } = tmpFile.target;

    // Store the original file for later upload to S3
    if (files && files[0]) {
      setOriginalFile(files[0]);
    }

    //  the file is read through the filereader object
    const fileReader = new FileReader();

    fileReader.onload = async (event) => {
      try {
        setIsProcessing(true);
        const { result } = event.target;
        const workbook = XLSX.read(result, { type: "binary" });

        let data = [];
        for (const sheet in workbook.Sheets) {
          // esline-disable-next-line
          if (workbook?.Sheets) {
            //  using sheet_to_json  method will be excel  into json  data
            data = data.concat(XLSX.utils.sheet_to_json(workbook.Sheets[sheet]));
            const tmpData = sortBy(data, function (o) {
              return o["Stop Type"] !== "Pickup";
            });
            const [resultArr, isError] = await checkErrors(tmpData);
            setError(isError);
            setArray(resultArr);
          }
        }
      } catch (e) {
        console.log("e", e);
      } finally {
        setIsProcessing(false);
      }
    };

    //  open the file in binary mode
    fileReader.readAsBinaryString(files[0]);
  };
  const getPickupIds = (item) => {
    if (item["Stop Type"] === "Pickup") {
      return String(item["Id"]);
    }
  };

  /**
   * ROUTE DATA IMPORT LOGIC SUMMARY:
   *
   * This function validates and processes each stop during import with the following priority order:
   *
   * 1. **DIRECT LAT/LONG (HIGHEST PRIORITY)**
   *    - If Stop Latitude AND Stop Longitude are provided in import file
   *    - Bypasses all saved location matching logic
   *    - Uses coordinates directly from import data
   *
   * 2. **SAVED LOCATION MATCHING**
   *    - If Stop Name AND Contact Phone are both present
   *    - Matches against saved locations using:
   *      - Location Name: Partial, case-insensitive match (ILIKE %name%)
   *      - Phone: Normalized comparison (removes non-digits, leading "1")
   *    - If match found: Uses saved location's address, lat, lng
   *
   * 3. **ADDRESS GEOCODING (FALLBACK)**
   *    - Uses Stop Address as-is
   *    - Will be geocoded later via AVL (Address Validation/Lookup) service
   *    - Occurs when no direct coordinates AND no saved location match
   *
   * Additional validations:
   * - Time window format validation
   * - Phone number format validation
   * - Package quantity validation
   * - Barcode uniqueness validation
   * - Pickup ID existence validation for dropoffs
   * - Address consistency validation for same stop IDs
   */
  const caseError = async (item, pickupIds, arr) => {
    let tpmItem = { ...item };
    let isError = false;
    if (tpmItem["Stop Type"].toLowerCase() === "dropoff") {
      if (tpmItem["Pickup Id"] && !pickupIds.includes(String(tpmItem["Pickup Id"]))) {
        isError = true;
        tpmItem = {
          ...tpmItem,
          errors: tpmItem.errors
            ? [...tpmItem.errors, "Pickup Id does not exist"]
            : ["Pickup Id does not exist"]
        };
      }
    }
    TIME_KEYS.forEach((column) => {
      // Check if the value exists and convert to string before calling replace
      if (!tpmItem[column]) {
        isError = true;
        tpmItem = {
          ...tpmItem,
          errors: tpmItem.errors
            ? [...tpmItem.errors, `${column} is missing or empty`]
            : [`${column} is missing or empty`]
        };
        return;
      }

      const timeString = String(tpmItem[column]).replace(":", "");
      tpmItem[column] = moment(timeString, "hmm").format("HH:mm");
      const isValid = /^(?:[01][0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/.test(
        tpmItem[column]
      );
      if (!isValid) {
        isError = true;
        tpmItem = {
          ...tpmItem,
          errors: tpmItem.errors
            ? [...tpmItem.errors, `${column} is invalid`]
            : [`${column} is invalid`]
        };
      }
    });
    PHONE_COLUMNS.forEach((column) => {
      if (tpmItem[column] === undefined) return;
      // Convert to string and validate
      const phoneValue = String(tpmItem[column]);
      if (!/^[0-9()-*#+ -]+$/.test(trim(phoneValue))) {
        isError = true;
        tpmItem = {
          ...tpmItem,
          errors: tpmItem.errors
            ? [...tpmItem.errors, `${column} is invalid`]
            : [`${column} is invalid`]
        };
      }
    });
    for (const column of COLUMN_CANNOT_NULL) {
      if (column === "Stop Address") {
        // **PRIORITY #1: Use direct lat/lng from import file if both are provided**
        // This bypasses any saved location matching logic
        const importedLat = Number(item[LAT_LNG_DATA[0]] || 0);
        const importedLng = Number(item[LAT_LNG_DATA[1]] || 0);

        if (importedLat && importedLng) {
          // **Use direct coordinates from import - update the item with the coordinates**
          // Stamp them onto THIS row. Looking the row up by Id (`arr.find`) and copying that
          // row over tpmItem silently replaced the row's own data whenever an Id repeats —
          // e.g. a file with one pickup row per route, both numbered Id 1, gave the second
          // route a copy of the first route's package totals and Internal Route ID.
          tpmItem = {
            ...tpmItem,
            "Stop Latitude": importedLat,
            "Stop Longitude": importedLng,
            "Direct Coordinates": <>🎯 Direct</> // Visual indicator
          };
          continue;
        }

        // **PRIORITY #2: If no direct coordinates, try saved location matching**
        // Only attempt saved location lookup if Stop Name and Phone are both present
        const stopName = item["Stop Name"];
        const contactPhone = item["Contact Phone 1"] || item["Contact Phone 2"];

        if (stopName && contactPhone) {
          try {
            const preSavedLocationResponse = await fetchPresavedLocation(item);
            if (preSavedLocationResponse?.preSaved) {
              // Found saved location match: Stop Name + Phone match.
              // As above: stamp the resolved address onto this row rather than copying over
              // it from the first row that happens to share its Id.
              tpmItem = {
                ...tpmItem,
                "Stop Address": preSavedLocationResponse?.address,
                "Stop Latitude": preSavedLocationResponse?.lat,
                "Stop Longitude": preSavedLocationResponse?.lng,
                "Saved Location": preSavedLocationResponse?.preSaved && <>✅ Saved</>
              };
              console.log(
                `Using saved location for ${item["Id"]}: ${stopName} + ${contactPhone} matched`
              );
              continue;
            }
          } catch (err) {
            console.log(err);
          }
        }

        // **PRIORITY #3: Use Stop Address as-is (will be geocoded later via AVL)**
        // This happens when:
        // - No direct lat/lng provided in import
        // - No saved location match found (either no Stop Name/Phone or no match)
        console.log(`Using address as-is for ${item["Id"]}: will geocode via AVL`);
      }
      if (!item[column]) {
        isError = true;
        tpmItem = {
          ...tpmItem,
          errors: tpmItem.errors
            ? [...tpmItem.errors, `${column} does not exist`]
            : [`${column} does not exist`]
        };
      }
    }
    const importedPackages = Object.keys(PACKAGE_DATA).filter((column) => item[column]);
    if (!importedPackages?.length) {
      isError = true;
      tpmItem = {
        ...tpmItem,
        errors: tpmItem.errors
          ? [...tpmItem.errors, `At least 1 package`]
          : [`At least 1 package`]
      };
    }

    // Validate barcode: ensure only ONE barcode per row across all package sizes
    const barcodeColumns = Object.keys(PACKAGE_DATA).map((packageKey) =>
      COLUMN_NAME_BARCODE_PACKAGE(PACKAGE_DATA[packageKey])
    );
    const presentBarcodes = barcodeColumns.filter((column) => item[column]);
    if (presentBarcodes.length > 1) {
      isError = true;
      tpmItem = {
        ...tpmItem,
        errors: tpmItem.errors
          ? [
              ...tpmItem.errors,
              `Only one barcode per row allowed. Found barcodes in: ${presentBarcodes.join(
                ", "
              )}`
            ]
          : [
              `Only one barcode per row allowed. Found barcodes in: ${presentBarcodes.join(
                ", "
              )}`
            ]
      };
    }

    /* Checks if stops with the same ID have the same stop address */
    arr.map((stop) => {
      // Compare current item with other stops to find others with the same ID
      if (String(tpmItem["Id"]) === String(stop["Id"])) {
        // If there's an ID match but addresses aren't the same, add the error
        if (tpmItem["Stop Address"] !== stop["Stop Address"]) {
          isError = true;
          tpmItem = {
            ...tpmItem,
            errors: tpmItem.errors
              ? [
                  ...tpmItem.errors,
                  ` Stops with the ID #${tpmItem["Id"]} have different stop addresses`
                ]
              : [`Stops with the ID #${tpmItem["Id"]} have different stop addresses`]
          };
        }
      }
    });
    return [tpmItem, isError];
  };

  const checkErrors = async (arr) => {
    // Validation for Package Counts by size - allow null / NaN but coerce to 0.
    let countOfDropoffPackagesMicro = 0;
    let countOfDropoffPackagesSmall = 0;
    let countOfDropoffPackagesMedium = 0;
    let countOfDropoffPackagesLarge = 0;
    let countOfDropoffPackagesPallet = 0;
    let countOfDropoffPackagesPostal = 0;

    let countOfPickupPackagesMicro = 0;
    let countOfPickupPackagesSmall = 0;
    let countOfPickupPackagesMedium = 0;
    let countOfPickupPackagesLarge = 0;
    let countOfPickupPackagesPallet = 0;
    let countOfPickupPackagesPostal = 0;

    let latestDropoffTimeEnd = handleTimeImport("00:00");
    let earliestPickupTimeStart = handleTimeImport("23:59");

    let isError = false;
    let tmpArr = [...arr];
    const pickupIds = map(tmpArr, getPickupIds).filter((n) => n);
    tmpArr = tmpArr.map(async (ar) => {
      const [item, err] = await caseError(ar, pickupIds, tmpArr);
      isError = isError || err;

      if (ar["Stop Type"].toLowerCase() === "pickup") {
        // Convert CSV string values to numbers for proper addition
        countOfPickupPackagesMicro += parseInt(ar["Total Micro Packages"] || 0, 10);
        countOfPickupPackagesSmall += parseInt(ar["Total Small Packages"] || 0, 10);
        countOfPickupPackagesMedium += parseInt(ar["Total Medium Packages"] || 0, 10);
        countOfPickupPackagesLarge += parseInt(ar["Total Large Packages"] || 0, 10);
        countOfPickupPackagesPallet += parseInt(ar["Total Pallet Packages"] || 0, 10);
        countOfPickupPackagesPostal += parseInt(ar["Total Postal Packages"] || 0, 10);

        console.log(
          `[AI Import] Pickup row - Small Packages: "${
            ar["Total Small Packages"]
          }" -> ${parseInt(
            ar["Total Small Packages"] || 0,
            10
          )} (Running total: ${countOfPickupPackagesSmall})`
        );

        if (handleTimeImport(ar["Stop Time Window - Start"]) < earliestPickupTimeStart) {
          earliestPickupTimeStart = handleTimeImport(ar["Stop Time Window - Start"]);
        }
      } else if (ar["Stop Type"].toLowerCase() === "dropoff") {
        // Convert CSV string values to numbers for proper addition
        countOfDropoffPackagesMicro += parseInt(ar["Total Micro Packages"] || 0, 10);
        countOfDropoffPackagesSmall += parseInt(ar["Total Small Packages"] || 0, 10);
        countOfDropoffPackagesMedium += parseInt(ar["Total Medium Packages"] || 0, 10);
        countOfDropoffPackagesLarge += parseInt(ar["Total Large Packages"] || 0, 10);
        countOfDropoffPackagesPallet += parseInt(ar["Total Pallet Packages"] || 0, 10);
        countOfDropoffPackagesPostal += parseInt(ar["Total Postal Packages"] || 0, 10);

        console.log(
          `[AI Import] Dropoff row - Small Packages: "${
            ar["Total Small Packages"]
          }" -> ${parseInt(
            ar["Total Small Packages"] || 0,
            10
          )} (Running total: ${countOfDropoffPackagesSmall})`
        );

        if (handleTimeImport(ar["Stop Time Window - End"]) > latestDropoffTimeEnd) {
          latestDropoffTimeEnd = handleTimeImport(ar["Stop Time Window - End"]);
        }
      }
      return item;
    });

    // resolves promises items in array
    tmpArr = await Promise.all(tmpArr);

    console.log("[AI Import] Package count validation:");
    console.log(
      `  Micro - Pickup: ${countOfPickupPackagesMicro}, Dropoff: ${countOfDropoffPackagesMicro}`
    );
    console.log(
      `  Small - Pickup: ${countOfPickupPackagesSmall}, Dropoff: ${countOfDropoffPackagesSmall}`
    );
    console.log(
      `  Medium - Pickup: ${countOfPickupPackagesMedium}, Dropoff: ${countOfDropoffPackagesMedium}`
    );
    console.log(
      `  Large - Pickup: ${countOfPickupPackagesLarge}, Dropoff: ${countOfDropoffPackagesLarge}`
    );
    console.log(
      `  Pallet - Pickup: ${countOfPickupPackagesPallet}, Dropoff: ${countOfDropoffPackagesPallet}`
    );
    console.log(
      `  Postal - Pickup: ${countOfPickupPackagesPostal}, Dropoff: ${countOfDropoffPackagesPostal}`
    );

    if (earliestPickupTimeStart >= latestDropoffTimeEnd) {
      isError = true;
      notification.error({
        message: "Validation error - Pickup Window is later than Dropoff Window.",
        placement: "topRight"
      });
    }

    // Compare for each size and throw specific error
    if (countOfDropoffPackagesMicro !== countOfPickupPackagesMicro) {
      isError = true;
      console.log(
        `[AI Import] Micro validation FAILED: ${countOfDropoffPackagesMicro} !== ${countOfPickupPackagesMicro}`
      );
      notification.error({
        message:
          "Validation error - Micro Packages count is incorrect. Please double check the pickup and dropoff counts for total number of micro packages.",
        placement: "topRight"
      });
    }

    if (countOfDropoffPackagesSmall !== countOfPickupPackagesSmall) {
      isError = true;
      console.log(
        `[AI Import] Small validation FAILED: ${countOfDropoffPackagesSmall} !== ${countOfPickupPackagesSmall}`
      );
      notification.error({
        message:
          "Validation error - Small Packages count is incorrect. Please double check the pickup and dropoff counts for total number of small packages.",
        placement: "topRight"
      });
    }

    if (countOfDropoffPackagesMedium !== countOfPickupPackagesMedium) {
      isError = true;
      notification.error({
        message:
          "Validation error - Medium Packages count is incorrect. Please double check the pickup and dropoff counts for total number of medium packages.",
        placement: "topRight"
      });
    }

    if (countOfDropoffPackagesLarge !== countOfPickupPackagesLarge) {
      isError = true;
      notification.error({
        message:
          "Validation error - Large Packages count is incorrect. Please double check the pickup and dropoff counts for total number of large packages.",
        placement: "topRight"
      });
    }

    if (countOfDropoffPackagesPallet !== countOfPickupPackagesPallet) {
      isError = true;
      notification.error({
        message:
          "Validation error - Pallet Packages count is incorrect. Please double check the pickup and dropoff counts for total number of pallet packages.",
        placement: "topRight"
      });
    }

    if (countOfDropoffPackagesPostal !== countOfPickupPackagesPostal) {
      isError = true;
      notification.error({
        message:
          "Validation error - Postal Packages count is incorrect. Please double check the pickup and dropoff counts for total number of postal packages.",
        placement: "topRight"
      });
    }

    return [tmpArr, isError];
  };

  const handleOnChangeImport = (e) => {
    onImportExcel(e);
  };

  const clickChooseFile = (e) => {
    fileInputRef.current.click();
  };

  const handleTimeImport = (time) => {
    // return moment(XLSX.SSF.parse_date_code(time)).subtract(1, "minutes");
    return moment(time, "HH:mm");
  };

  const handleConvertTime = (time) => {
    return moment(XLSX.SSF.parse_date_code(time)).subtract(1, "minutes").format("HH:mm");
  };

  const handleImport = async () => {
    try {
      dispatch(setGlobalLoading({ isLoading: true }));
      const pickups = [];
      const dropoffs = [];
      let packages = [];
      let places = { places: {} };

      // Client is required for import file in admin
      if (isAdmin && !clientId.length) {
        dispatch(setGlobalLoading({ isLoading: false }));
        return notification.error({
          message: "Please select a client."
        });
      }

      // Check if selected client is blocked (for admin only)
      if (isAdmin && clientId) {
        const selectedClient = allClients.find((client) => client.id === clientId);
        if (selectedClient && selectedClient.isBlocked) {
          const confirmed = await new Promise((resolve) => {
            Modal.confirm({
              title: "Warning: Client is Blocked",
              content: (
                <div>
                  <p
                    style={{ color: "#ff4d4f", fontWeight: "bold", marginBottom: "12px" }}
                  >
                    ⚠️ The selected client &quot;{selectedClient.clientName}&quot; is
                    currently blocked.
                  </p>
                  <p>
                    <strong>Blocked At:</strong>{" "}
                    {selectedClient.blockedAt
                      ? new Date(selectedClient.blockedAt).toLocaleString()
                      : "N/A"}
                  </p>
                  <p>
                    <strong>Blocked By:</strong> {selectedClient.blockedBy || "N/A"}
                  </p>
                  {selectedClient.blockReason && (
                    <p>
                      <strong>Reason:</strong> {selectedClient.blockReason}
                    </p>
                  )}
                  <p style={{ marginTop: "12px" }}>
                    Do you want to proceed with creating routes for this blocked client?
                  </p>
                </div>
              ),
              okText: "Yes, Proceed",
              cancelText: "Cancel",
              okType: "danger",
              icon: null,
              width: 500,
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            });
          });

          if (!confirmed) {
            dispatch(setGlobalLoading({ isLoading: false }));
            return;
          }
        }
      }
      // **PREPARE LOCATION DATA FOR AVL GEOCODING**
      // For each stop, prepare data for Address Validation/Lookup (AVL) service
      // Direct lat/lng coordinates from import take precedence over geocoding
      array.forEach((item) => {
        const addr = item["Stop Address"] || item["Stop Name"];
        const businessName = item["Stop Name"];
        const phone = item["Contact Phone 1"];
        places.places[addr] = { address: addr };
        places.places[addr].business_name = businessName;
        places.places[addr].phone = phone;
        // If admin use selected client, if client view use client id from profile
        places.places[addr].clientId = isAdmin ? clientId : profile.client.id;

        // **PRIORITIZE DIRECT COORDINATES FROM IMPORT FILE**
        // If lat/lng provided in import, use them directly (bypasses geocoding)
        let latitude = Number(item[LAT_LNG_DATA[0]] || 0);
        let longitude = Number(item[LAT_LNG_DATA[1]] || 0);
        if (latitude && longitude) {
          places.places[addr]["latitude"] = latitude;
          places.places[addr]["longitude"] = longitude;
          // Mark as having direct coordinates to ensure proper handling
          places.places[addr]["directCoordinates"] = true;
          console.log(
            `Setting direct coordinates in places for ${addr}: lat=${latitude}, lng=${longitude}`
          );
        }
      });

      const avlResponse = await axios.post(window.REACT_APP_GEOCODE_API_URI, places);
      if (_.isEmpty(avlResponse.data.places)) {
        dispatch(setGlobalLoading({ isLoading: false }));
        handleCloseModal(false);
        return notification.error({
          message:
            "There was an error validating address. Please make sure your addresses are well formatted.",
          duration: 5
        });
      }

      // Process addresses - use default coordinates for low precision addresses
      const DEFAULT_LAT = 18.413451;
      const DEFAULT_LNG = -66.1008963;

      for (const [resKey, resValue] of Object.entries(avlResponse.data.places)) {
        // **Skip precision validation for addresses with direct coordinates**
        // If we sent direct coordinates to AVL, don't treat them as low precision
        const hasDirectCoordinates = places.places[resKey]?.directCoordinates;

        if (
          !hasDirectCoordinates &&
          (!Object.keys(resValue).length || resValue?.precision === "low")
        ) {
          // Set default coordinates for low precision addresses
          avlResponse.data.places[resKey] = {
            ...resValue,
            latitude: DEFAULT_LAT,
            longitude: DEFAULT_LNG,
            precision: "default",
            originalPrecision: "low"
          };
          console.log(
            `[Import] Using default coordinates for low precision address: ${resKey}`
          );
        }

        if (hasDirectCoordinates) {
          console.log(`AVL Response for direct coordinates ${resKey}:`, {
            sent: places.places[resKey],
            received: resValue
          });
        }
      }

      // Clear any previous AVL errors
      setAVLRowAddressError([]);

      // Upload the original import file to S3
      let importFileId = null;
      if (originalFile) {
        try {
          const formData = new FormData();
          formData.append("file", originalFile);

          const cookies = new Cookies();
          const token = cookies.get(TOKEN_NAME, { path: "/" });

          const uploadResponse = await axios.post(
            `${BASE_URL}/routes/upload-import-file`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`
              }
            }
          );

          importFileId = uploadResponse.data.id;
          console.log("Import file uploaded to S3:", importFileId);
        } catch (uploadError) {
          console.error("Failed to upload import file:", uploadError);
          // Continue anyway - file upload is optional
        }
      }

      // Note: Barcode handling is now done directly during dropoff processing

      setTotalStop?.(array.length);
      for (let i = 0; i < array.length; i++) {
        setCurrentStop?.(i + 1);
        const item = array[i];
        const id = String(item["Id"]);
        let location = {};
        const keys = Object.keys(item);
        const addr = item["Stop Address"] || item["Stop Name"];
        const place_response = avlResponse?.data?.places?.[addr];

        // **PRIORITY: Use direct coordinates from import file if available**
        const importedLat = Number(item[LAT_LNG_DATA[0]] || 0);
        const importedLng = Number(item[LAT_LNG_DATA[1]] || 0);

        let latitude, longitude;
        if (importedLat && importedLng) {
          // Use direct coordinates from import file
          latitude = importedLat;
          longitude = importedLng;
          console.log(
            `Final processing: Using direct coordinates for ${id}: lat=${latitude}, lng=${longitude}`
          );
        } else {
          // Fall back to AVL geocoded coordinates
          latitude = place_response?.latitude;
          longitude = place_response?.longitude;
          console.log(
            `Final processing: Using AVL coordinates for ${id}: lat=${latitude}, lng=${longitude}`
          );
        }

        let address = {
          address: item["Stop Address"],
          lngLat: [longitude || 0, latitude || 0]
        };

        console.log(`Address object for ${id}:`, {
          address: address.address,
          lngLat: address.lngLat,
          rawLat: latitude,
          rawLng: longitude,
          importedLat: importedLat,
          importedLng: importedLng
        });
        keys.forEach((key) => {
          if (MAP_CSV_DATA[key]) {
            location = {
              ...location,
              [MAP_CSV_DATA[key]]: item[key]
            };
          }
        });
        let phonNumbers = [
          item["Contact Phone"],
          item["Contact Phone 1"],
          item["Contact Phone 2"]
        ].filter((n) => n);
        const phone_numbers = phonNumbers.map((phone) => {
          let trimmedPhone = trim(phone);
          if (trimmedPhone.match(/^(787)|^(939)/)) {
            trimmedPhone = "+1" + trimmedPhone;
          }
          return {
            type: "contact",
            phone: (trimmedPhone?.indexOf("+") === -1 ? "+" : "") + trimmedPhone
          };
        });

        const packageName = item["Item Name"];
        if (item["Stop Type"].toLowerCase() === "pickup") {
          Object.keys(PACKAGE_DATA).forEach((packageKey) => {
            if (item[packageKey]) {
              const packageId = generateId("PickupPack");
              const genericName = PACKAGE_DATA[packageKey];
              const packageType = packageTypeList.find(
                (pack) => pack.name === genericName
              );

              // For pickup packages, only use barcode from pickup row itself
              let assignedBarcode = item[COLUMN_NAME_BARCODE_PACKAGE(genericName)];
              console.log(
                `Pickup ${id} - ${genericName}: assigned barcode = ${assignedBarcode}`
              );

              const quantity = parseInt(item[packageKey] || 0, 10);
              console.log(
                `[AI Import] Pickup package - ${genericName}: "${
                  item[packageKey]
                }" -> ${quantity} (type: ${typeof quantity})`
              );

              packages.push({
                id: packageId,
                name: packageName || genericName,
                action: null,
                quantity: quantity, // Convert to integer
                packageTypeId: packageType?.id,
                maxLength: packageType?.maxLength,
                maxWeight: packageType?.maxWeight,
                maxVolume: packageType?.maxVolume,
                pickupId: id,
                barcode: assignedBarcode
              });

              console.log(
                `Import: Created package ${packageId} with barcode: ${assignedBarcode}`
              );
            }
          });

          const pickMaxPosition = Math.max(...pickups.map((p) => p.position), 0);

          if (findIndex(pickups, { id }) !== -1) continue;

          const pickupObject = {
            ...location,
            id,
            address,
            // If we update address, leave the old address as future reference for drivers
            physical_address: item["Physical Address"] || address.address,
            from_time: handleTimeImport(item["Stop Time Window - Start"]),
            to_time: handleTimeImport(item["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: pickMaxPosition + 1
          };

          console.log(`PICKUP OBJECT CREATED for ${id}:`, {
            id: pickupObject.id,
            lat: pickupObject.lat,
            lng: pickupObject.lng,
            address: pickupObject.address,
            lngLat: pickupObject.address.lngLat
          });

          pickups.push(pickupObject);
        } else {
          Object.keys(PACKAGE_DATA).forEach((packageKey) => {
            if (item[packageKey]) {
              const genericName = PACKAGE_DATA[packageKey];
              const dropoffBarcode = item[COLUMN_NAME_BARCODE_PACKAGE(genericName)];
              const dropoffPackageName = item["Item Name"];

              // If this dropoff has a specific barcode, create a separate package instance
              if (dropoffBarcode) {
                const packageId = generateId("DropoffPack");
                const packageType = packageTypeList.find(
                  (pack) => pack.name === genericName
                );

                const quantity = parseInt(item[packageKey] || 0, 10);
                console.log(
                  `[AI Import] Dropoff package (with barcode) - ${genericName}: "${
                    item[packageKey]
                  }" -> ${quantity} (type: ${typeof quantity})`
                );

                // Create a new package instance specifically for this dropoff
                packages.push({
                  id: packageId,
                  name: dropoffPackageName || genericName,
                  action: null,
                  quantity: quantity, // Convert to integer
                  packageTypeId: packageType?.id,
                  maxLength: packageType?.maxLength,
                  maxWeight: packageType?.maxWeight,
                  maxVolume: packageType?.maxVolume,
                  pickupId: String(item["Pickup Id"]),
                  barcode: dropoffBarcode,
                  dropoffs: [
                    {
                      id,
                      quantity: quantity, // Convert to integer
                      barcode: dropoffBarcode
                    }
                  ]
                });

                console.log(
                  `Dropoff ${id} - created separate package ${packageId} with barcode: ${dropoffBarcode}`
                );
              } else {
                // No specific barcode for this dropoff, use the existing shared package logic
                const packagePickup = packages.find(
                  (pack) =>
                    String(pack.pickupId) === String(item["Pickup Id"]) &&
                    pack.name === (dropoffPackageName || genericName) &&
                    !pack.barcode // Only match packages without specific barcodes
                );

                if (packagePickup) {
                  const quantity = parseInt(item[packageKey] || 0, 10);
                  console.log(
                    `[AI Import] Dropoff package (shared) - ${genericName}: "${
                      item[packageKey]
                    }" -> ${quantity} (type: ${typeof quantity})`
                  );

                  const packageWithDropoff = {
                    ...packagePickup,
                    dropoffs:
                      packagePickup?.dropoffs?.length > 0
                        ? [
                            ...packagePickup?.dropoffs,
                            {
                              id,
                              quantity: quantity // Convert to integer
                            }
                          ]
                        : [
                            {
                              id,
                              quantity: quantity // Convert to integer
                            }
                          ]
                  };
                  packages = replaceElementInArray(packages, packageWithDropoff);
                  console.log(
                    `Dropoff ${id} - added to existing package ${packagePickup.id} without barcode`
                  );
                }
              }
            }
          });
          const dropMaxPosition = Math.max(...dropoffs.map((p) => p.position), 0);
          if (findIndex(dropoffs, { id }) !== -1) continue;

          const dropoffObject = {
            ...location,
            id,
            address,
            // If we update address, leave the old address as future reference for drivers
            physical_address: item["Physical Address"] || address.address,
            from_time: handleTimeImport(item["Stop Time Window - Start"]),
            to_time: handleTimeImport(item["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: dropMaxPosition + 1
          };

          console.log(`DROPOFF OBJECT CREATED for ${id}:`, {
            id: dropoffObject.id,
            lat: dropoffObject.lat,
            lng: dropoffObject.lng,
            address: dropoffObject.address,
            lngLat: dropoffObject.address.lngLat
          });

          dropoffs.push(dropoffObject);
        }
      }

      // Drop the pickup rows' bundle packages that nothing was ever allocated against.
      //
      // A pickup row declares totals ("Total Micro Packages: 17") and each dropoff row claims
      // a share of them. When a dropoff has no barcode it is appended to the pickup's bundle
      // via `dropoffs[]`, so the bundle is the real package. But when a dropoff carries its
      // own barcode it gets a package of its own instead, and the pickup's bundle is left
      // with an empty `dropoffs` — a placeholder that is delivered nowhere yet still counts
      // against the load. That is the "17 x 2" bundle pair users have been deleting by hand
      // on every barcoded import.
      //
      // No `dropoffs` means no stop receives it, so such a package is never legitimate — and
      // it cannot be a row that simply declared no dropoffs, because caseError already
      // rejects any row without at least one package and any file whose pickup totals do not
      // match its dropoff totals.
      const deliverablePackages = packages.filter((pack) => pack.dropoffs?.length);
      const discarded = packages.length - deliverablePackages.length;
      if (discarded) {
        console.log(
          `Import: dropped ${discarded} unallocated pickup bundle package(s); ` +
            `their quantities are already covered by the per-dropoff packages.`
        );
      }

      dispatch(
        saveAsDraftNewOrder({
          data: {
            clientId,
            pickups,
            dropoffs,
            packages: deliverablePackages,
            importFileId
          }
        })
      );
      dispatch(resetNewOrderStatuses());
      notification.success({
        message: "Import successfully!",
        placement: "topRight"
      });

      // Navigate to route planning page with imported data
      history.push(isAdmin ? "/admin/jobs/new?action=new" : "/orders/new?action=new");
      handleCloseModal(false);
      dispatch(setGlobalLoading({ isLoading: false }));
    } catch (importError) {
      console.error("[Import] Error during import:", importError);
      dispatch(setGlobalLoading({ isLoading: false }));
      notification.error({
        message: "Import failed",
        description:
          importError.message || "An error occurred during import. Please try again.",
        placement: "topRight"
      });
    }
  };

  const headerKeys = Object.keys(Object.assign({}, ...array)).filter(
    (k) => k !== "errors"
  );

  // Add consolidated barcode column if any barcode columns exist, but remove individual barcode columns
  const barcodeColumns = Object.keys(PACKAGE_DATA).map((packageKey) =>
    COLUMN_NAME_BARCODE_PACKAGE(PACKAGE_DATA[packageKey])
  );
  const hasBarcodes = headerKeys.some((key) => barcodeColumns.includes(key));

  const displayHeaders = headerKeys
    .filter((key) => !barcodeColumns.includes(key))
    // Put Saved Location column first in table
    .sort((x, y) => (x === "Saved Location" ? -1 : y === "Saved Location" ? 1 : 0));
  if (hasBarcodes) {
    displayHeaders.push("Barcode");
  }

  // Helper function to get the barcode value from any barcode column for a row
  const getBarcodeValue = (item) => {
    return barcodeColumns.find((column) => item[column])
      ? item[barcodeColumns.find((column) => item[column])]
      : "";
  };

  /**
   * @description This will add new submitted address to array of order
   * @param {*} origAddress Old address to update
   * @param {*} submittedAddr New addres to update
   *
   */
  const saveAutoCompleteAddress = (origAddress, submittedAddr, selectedAddressObj) => {
    // Check if same address
    if (origAddress === submittedAddr) {
      return notification.error({
        message: "Please do not submit same address. Update address for better precision."
      });
    }

    // Turn array of address data to object by Id
    const arrayToObjById = Object.fromEntries(array.map((arr) => [arr["Id"], arr]));

    // Delete errors
    delete arrayToObjById[importRowIdSelected].errors;

    // Add to valid address the old address entries/data
    arrayToObjById[importRowIdSelected] = {
      ...arrayToObjById[importRowIdSelected],
      "Stop Address": submittedAddr,
      "Physical Address": origAddress,
      [LAT_LNG_DATA[0]]: selectedAddressObj.lngLat[1],
      [LAT_LNG_DATA[1]]: selectedAddressObj.lngLat[0]
    };

    const hasArrayErrors = Object.keys(arrayToObjById).filter((key) => {
      return arrayToObjById[key].errors;
    });

    if (!hasArrayErrors.length) {
      setError(false);
    }

    // Create array and sort the array because updated object is pushed back
    // Affects when in create order tries to look for the pickup and there is dropoff instead
    const newUpdatedArray = Object.values(arrayToObjById).sort((a, b) => a.Id - b.Id);

    setArray(newUpdatedArray);
  };

  /**
   * Check if returned addresses from AVL has errors
   * @param {*} rowAddress
   * @returns boolean
   */
  const doesAVLHasErrors = (rowAddress) => {
    return avlRowAddressError.includes(rowAddress);
  };

  /**
   * @description This will update map address state by choosing from input or map when there is an AVL error.
   * @param selectedAddress Address selected by either input or map
   */
  const selectAddressMapInput = useCallback((selectedAddress) => {
    const currentSelectedAddress = mapAddress.address;
    const newSelectedAddress = selectedAddress.address;

    saveAutoCompleteAddress(currentSelectedAddress, newSelectedAddress, selectedAddress);
    setMapAddress(selectedAddress);

    // Here we are removing address with error after updating it
    // in order to enable import button and submit again to AVL.
    const tmpAVLRowAddressError = avlRowAddressError;
    const indexAddr = tmpAVLRowAddressError.indexOf(currentSelectedAddress);
    if (indexAddr > -1) {
      tmpAVLRowAddressError.splice(indexAddr, 1);
    }
    setMapAVLError(!!tmpAVLRowAddressError.length);
    setAVLRowAddressError(tmpAVLRowAddressError);
  });

  const fetchPresavedLocation = async (item) => {
    const preSavedLocationResponse = await request.post(
      "/saved-locations/pre-saved-locations",
      {
        locationName: item["Stop Name"],
        phone: item["Contact Phone 1"] || item["Contact Phone 2"]
      }
    );
    return preSavedLocationResponse;
  };

  const editRowBadAddress = (item) => {
    setImportRowIdSelected(item["Id"]);
    setShowEditAddressModal(!showEditAddressModal);
    setMapAddress({
      address: item["Stop Address"] || item["Stop Name"],
      lngLat: [item[LAT_LNG_DATA[1]] || 0, item[LAT_LNG_DATA[0]] || 0]
    });
  };

  return (
    <>
      <div className="import-locations-component pb-4">
        <div className="m-2">
          <div className="import-main-container">
            <div>
              <p className="import-modal-title">
                {aiImportData ? "Review AI Import Data" : "Upload Route Data"}
              </p>
              <p className="import-sub-title">
                {aiImportData
                  ? "Review and verify the imported route data before proceeding."
                  : "Easily import your route details for seamless optimization."}
              </p>
              {!aiImportData && (
                <p className="import-description">Supported formats: CSV, XLSX</p>
              )}

              <div style={{ display: "flex", justifyContent: "center" }}>
                {isAdmin && (
                  <Select
                    className="group-select-custom"
                    size="large"
                    placeholder="Please Select"
                    showSearch={true}
                    optionFilterProp="children"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(value) => {
                      setClientId(value);
                    }}
                    defaultValue={""}
                    style={{
                      width: "100%"
                    }}
                  >
                    <Select.Option value={""}>Choose a client</Select.Option>
                    {allClients.map((userClient) => (
                      <Select.Option key={userClient.id} value={userClient.id}>
                        {userClient.clientName}
                        <span className="hint">
                          {userClient.supportEmail ? ` (${userClient.supportEmail})` : ""}
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </div>

              {!aiImportData && (
                <>
                  <label htmlFor="csvFileInput" className="custom-import-file-upload">
                    <div className="inner-file-upload">
                      <div>
                        <p>Click to browse or drag and drop your CSV files</p>
                        <CloudUploadOutlined
                          style={{ fontSize: "5em", color: "#1278f1" }}
                        />
                      </div>
                    </div>

                    <div className="sample-download-link-wrapper">
                      <a
                        target="_blank"
                        href="https://flexio-media.s3.amazonaws.com/sample-xlsx/sample.xlsx"
                        rel="noopener noreferrer"
                        className="sample-download-link"
                      >
                        Download Sample Files
                      </a>
                    </div>
                  </label>

                  <div style={{ marginTop: "20px", textAlign: "center" }}>
                    <Tooltip title="Coming soon - Import route data from exported JSON files">
                      <Button
                        size="large"
                        disabled
                        style={{
                          width: "300px",
                          backgroundColor: "#f0f0f0",
                          borderColor: "#d9d9d9",
                          color: "#999",
                          cursor: "not-allowed"
                        }}
                      >
                        Import JSON (Coming Soon)
                      </Button>
                    </Tooltip>
                  </div>
                  <input
                    ref={fileInputRef}
                    type={"file"}
                    id={"csvFileInput"}
                    accept={".xlsx, .xls"}
                    onChange={handleOnChangeImport}
                    className="hidden file-import"
                  />
                </>
              )}
            </div>
            {/* <Tooltip
              placement="right"
              overlayStyle={{ maxWidth: "600px" }}
              title={
                <span>
                  Click here to import your own <b>.xlsx</b> file and add routes in bulk.
                </span>
              }
            >
              <div className="d-flex mb-3 ant-space-align-center">
                <QuestionIcon />
              </div>
            </Tooltip> */}
          </div>
          {/* <Card bodyStyle={{ backgroundColor: "#f2f2f2" }}>
            <p>
              The following describes the requirements for importing an <b>.xlsx</b> file:
            </p>
            <p>Mandatory fields:</p>
            <ul>
              <li>Id</li>
              <li>Stop Type</li>
              <li>
                Pickup Id (must correspond to the Pickup id in case of dropoffs and - in
                case of being a pickup)
              </li>
              <li>Contact Phone 1</li>
              <li>Stop Address</li>
              <li>Stop Time Window - Start</li>
              <li>Stop Time Window - End</li>
              <li>Waiting/Service Time</li>
            </ul>
            <p>
              Additionally, it is necessary to provide one of the following data, which
              will depend on the package type:
            </p>
            <ul>
              <li>Total Micro Packages</li>
              <li>Total Small Packages</li>
              <li>Total Medium Packages</li>
              <li>Total Large Packages</li>
              <li>Total Pallet Packages</li>
            </ul>
          </Card> */}

          {/* TODO: Refactor table to antd Table */}
          <div className="table-wrapper">
            <table id="client-table">
              <thead>
                <tr key={"header"}>
                  {displayHeaders.map((key) => (
                    <th key={key} className="px-3 header-table">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {array.map((item) =>
                  item.errors ? (
                    <tr
                      key={item[ID_INDEX]}
                      className={item.errors && "errors"}
                      style={{
                        backgroundColor:
                          doesAVLHasErrors(item["Stop Address"] || item["Stop Name"]) &&
                          "#f98e8e"
                      }}
                    >
                      {displayHeaders.map((key) => (
                        <Tooltip
                          placement="top"
                          title={String(item.errors || "")}
                          key={key}
                        >
                          <td className="px-3">
                            {key === "Stop Address" ? (
                              <>
                                {item[key]}
                                {""}
                                <EditFilled
                                  onClick={() => {
                                    editRowBadAddress(item);
                                  }}
                                />
                              </>
                            ) : key === "Barcode" ? (
                              getBarcodeValue(item)
                            ) : (
                              item[key]
                            )}
                          </td>
                        </Tooltip>
                      ))}
                    </tr>
                  ) : (
                    <tr
                      key={item[ID_INDEX]}
                      style={{
                        backgroundColor:
                          doesAVLHasErrors(item["Stop Address"] || item["Stop Name"]) &&
                          "#f98e8e"
                      }}
                    >
                      {displayHeaders.map((key) => (
                        <td
                          className={`${item["Saved Location"] ? "data-pre-saved" : ""}`}
                          key={key}
                        >
                          {key === "Stop Address" &&
                          doesAVLHasErrors(item["Stop Address"] || item["Stop Name"]) ? (
                            <>
                              {item[key]}
                              {""}
                              <EditFilled
                                onClick={() => {
                                  editRowBadAddress(item);
                                }}
                              />
                            </>
                          ) : key === "Barcode" ? (
                            getBarcodeValue(item)
                          ) : (
                            item[key]
                          )}
                        </td>
                      ))}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          {array?.length > 0 && (
            <div className="d-flex flex-justify-end">
              <Button
                type="primary"
                className="btn-download svg-icon mb-3 mr-2"
                onClick={handleImport}
                disabled={mapAVLError}
              >
                Import
              </Button>
            </div>
          )}
          {!array?.length &&
            console.log(
              "[AI Import] Import button NOT shown - array length:",
              array?.length
            )}
        </div>
      </div>
      <Modal
        title="Edit Address"
        open={showEditAddressModal}
        onCancel={() => setShowEditAddressModal(false)}
        okText="Edit"
        onOk={() => {
          setShowEditAddressModal(false);
        }}
      >
        <AddressInput
          placeholder="Address"
          onSelect={(selectedAddress) => {
            selectAddressMapInput(selectedAddress);
          }}
          address={mapAddress}
        />
        <div className="map mt-4">
          <DefaultMapbox
            address={mapAddress}
            setAddress={(selectedAddress) => {
              selectAddressMapInput(selectedAddress);
            }}
          />
        </div>
      </Modal>

      {/* Processing Modal */}
      <Modal open={isProcessing} closable={false} footer={null} centered width={400}>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div
            style={{
              fontSize: "48px",
              marginBottom: "20px",
              animation: "spin 2s linear infinite"
            }}
          >
            ⏳
          </div>
          <h3 style={{ marginBottom: "10px" }}>Processing...</h3>
          <p style={{ color: "#666" }}>Checking addresses...</p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </Modal>
    </>
  );
};

export default ImportLocations;
